import React, { useState, useEffect } from "react";
import { useCart } from "react-use-cart";
import { BsCartPlus, BsHeart, BsHeartFill, BsStarFill, BsTruck, BsShieldCheck, BsArrowLeft } from "react-icons/bs";
import { db } from "../firebaseConfig";
import { useParams, useNavigate } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { formatPrice } from "../utils/priceFormatter";
// Removed Stripe imports - now using Flutterwave
import CheckoutForm from "./CheckoutForm";
import Shop from "./Shop";
import { HotProductsRow } from "./Hero";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [Show, setShow] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [productId]);

  useEffect(() => {
    async function getProductData() {
      const docRef = db.collection("Products").doc(productId);
      const doc = await docRef.get();

      if (doc.exists) {
        const data = doc.data();
        const colors =
          typeof data.color === "string" ? data.color.split(",") : [];
        setProductData({ id: doc.id, ...data, colors });
      } else {
        console.log("No such document!");
        setProductData(null);
      }
    }

    getProductData();
  }, [productId]);

  // Removed Stripe configuration - now using Flutterwave

  const handleAddToCart = () => {
    try {
      if (!productData) {
        console.error('No product data available');
        return;
      }
      
      const productToAdd = {
        id: productData.id,
        product_name: productData.product_name || productData.title || productData.name,
        product_media: productData.product_media || productData.product_image || [],
        price: productData.price || productData.cost || 0,
        quantity: quantity,
        selectedSize: selectedSize,
        description: productData.description || productData.product_description,
        category: productData.category,
        ...productData // Include all other properties
      };
      
      console.log('Adding to cart from product details:', productToAdd);
      addItem(productToAdd);
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };



  if (!productData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gradient-to-r from-black to-amber-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 dark:border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gradient-to-r from-black to-amber-900 min-h-screen">
      {/* Header */}
      <div className=" pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 dark:text-amber-300 hover:text-gray-900 dark:hover:text-amber-100 transition-colors"
            >
              <BsArrowLeft className="text-lg" />
              Back
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Product Details</h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            {productData.product_image && productData.product_image.length > 0 ? (
              <div className="bg-white dark:bg-amber-900/20 rounded-2xl shadow-sm dark:shadow-amber-700/30 overflow-hidden border border-amber-200 dark:border-amber-700">
                <Carousel 
                  showArrows={true} 
                  showThumbs={true}
                  showStatus={false}
                  showIndicators={true}
                  infiniteLoop={true}
                  autoPlay={false}
                  interval={5000}
                  stopOnHover={true}
                  swipeable={true}
                  emulateTouch={true}
                  className="product-carousel"
                >
                  {productData.product_image.map((image, index) => (
                    <div key={index} className="aspect-square">
                      <img 
                        src={image} 
                        alt={`${productData.product_name || productData.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </Carousel>
              </div>
            ) : (
              <div className="bg-white dark:bg-amber-900/20 rounded-2xl shadow-sm dark:shadow-amber-700/30 aspect-square flex items-center justify-center border border-amber-200 dark:border-amber-700">
                <p className="text-gray-500 dark:text-amber-200">No images available</p>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-amber-900/20 rounded-2xl shadow-sm dark:shadow-amber-700/30 p-8 border border-amber-200 dark:border-amber-700">
              {/* Product Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  {/* Category */}
                  {productData?.category && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mb-2">
                      {productData.category}
                    </p>
                  )}
                  
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {productData?.product_name || productData?.title}
                  </h1>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <BsStarFill key={i} className="w-4 h-4" />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-amber-200">(4.5) • 128 reviews</span>
                  </div>
                  
                  {/* Stock Status */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-2 h-2 rounded-full ${
                      (productData?.quantity || 0) > 0 ? "bg-green-500" : "bg-red-500"
                    }`}></div>
                    <span className="text-sm text-gray-600 dark:text-amber-200">
                      {(productData?.quantity || 0) > 0 ? "In Stock" : "Out of Stock"}
                    </span>
                    {productData?.quantity && (
                      <span className="text-xs text-gray-500 dark:text-amber-300">
                        ({productData.quantity} available)
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={toggleFavorite}
                  className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-amber-800/50 transition-colors"
                >
                  {isFavorite ? (
                    <BsHeartFill className="text-red-500 text-xl" />
                  ) : (
                    <BsHeart className="text-gray-400 dark:text-amber-300 text-xl hover:text-red-500" />
                  )}
                </button>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatPrice(productData?.price)}
                  </span>
                  <span className="text-lg text-gray-400 dark:text-gray-500 line-through">
                    {formatPrice(productData.old_price || productData.price)}
                  </span>
                </div>
                {productData.old_price && productData.old_price > productData.price && (
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                    Save {formatPrice(productData.old_price - productData.price)}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={handleAddToCart}
                  disabled={(productData?.quantity || 0) <= 0}
                  className="w-full bg-amber-600 dark:bg-amber-700 text-white py-4 px-4 rounded-xl font-semibold hover:bg-amber-700 dark:hover:bg-amber-600 disabled:bg-gray-300 dark:disabled:bg-amber-800/50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  <BsCartPlus className="text-xl flex-shrink-0" />
                  <span className="truncate">{(productData?.quantity || 0) <= 0 ? "Out of Stock" : "Add to Cart"}</span>
                </button>

                <button
                  onClick={() => setShow(!Show)}
                  disabled={(productData?.quantity || 0) <= 0}
                  className="w-full bg-gray-900 dark:bg-amber-800 text-white py-4 px-4 rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-amber-700 disabled:bg-gray-300 dark:disabled:bg-amber-800/50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <span className="truncate">{(productData?.quantity || 0) <= 0 ? "Out of Stock" : "Buy Now"}</span>
                </button>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Quantity</h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-300 dark:border-amber-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-amber-800/30 text-gray-700 dark:text-amber-200"
                  >
                    -
                  </button>
                  <span className="w-16 text-center font-medium text-gray-900 dark:text-white">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-lg border border-gray-300 dark:border-amber-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-amber-800/30 text-gray-700 dark:text-amber-200"
                  >
                    +
                  </button>
                </div>
              </div>



              {/* Features */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-amber-700">
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center gap-3">
                    <BsShieldCheck className="text-green-600 dark:text-green-400 text-xl" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Secure Payment</p>
                      <p className="text-sm text-gray-500 dark:text-amber-200">100% secure checkout</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white dark:bg-amber-900/20 rounded-2xl shadow-sm dark:shadow-amber-700/30 p-8 border border-amber-200 dark:border-amber-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Description</h3>
              <DescriptionBlock text={productData?.product_description || productData?.description} />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {Show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-amber-900/20 rounded-2xl p-8 max-w-md w-full border border-amber-200 dark:border-amber-700">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Complete Purchase</h3>
            <CheckoutForm 
              amount={productData?.price}
              productData={productData}
              quantity={quantity}
              customer={{
                name: '',
                email: '',
                phone_number: ''
              }}
              onSuccess={(response) => {
                console.log('Payment successful:', response);
                setShow(false);
                // Handle successful payment
              }}
              onClose={() => setShow(false)}
            />
            <button
              onClick={() => setShow(false)}
              className="mt-4 w-full bg-gray-200 dark:bg-amber-800 text-gray-800 dark:text-amber-200 py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-amber-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Hot Products Row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <HotProductsRow />
      </div>

      {/* Related Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Shop title={'me'} />
      </div>
    </div>
  );
};

// Renders product description with bullet list and subheading support
function DescriptionBlock({ text }) {
  if (!text) {
    return <p className="text-gray-600 dark:text-amber-200 leading-relaxed">No description available.</p>;
  }

  const lines = text.split('\n');

  return (
    <div className="text-gray-600 dark:text-amber-200 leading-relaxed space-y-2">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <br key={i} />;
        // Subheading: lines starting with ## or **text**
        if (/^#{1,3}\s/.test(trimmed)) {
          return (
            <p key={i} className="font-bold text-gray-900 dark:text-white text-base mt-4">
              {trimmed.replace(/^#{1,3}\s/, '')}
            </p>
          );
        }
        if (/^\*\*(.+)\*\*$/.test(trimmed)) {
          return (
            <p key={i} className="font-bold text-gray-900 dark:text-white text-base mt-4">
              {trimmed.replace(/^\*\*|\*\*$/g, '')}
            </p>
          );
        }
        // Bullet: lines starting with - or • or *
        if (/^[-•*]\s/.test(trimmed)) {
          return (
            <div key={i} className="flex items-start gap-2 ml-2">
              <span className="text-amber-500 mt-1 flex-shrink-0">•</span>
              <span>{trimmed.replace(/^[-•*]\s/, '')}</span>
            </div>
          );
        }
        // Numbered list
        if (/^\d+\.\s/.test(trimmed)) {
          const match = trimmed.match(/^(\d+)\.\s(.+)/);
          return (
            <div key={i} className="flex items-start gap-2 ml-2">
              <span className="text-amber-500 font-semibold flex-shrink-0">{match[1]}.</span>
              <span>{match[2]}</span>
            </div>
          );
        }
        return <p key={i}>{trimmed}</p>;
      })}
    </div>
  );
}

export default ProductDetails;
